import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().trim().email({ message: "Please enter a valid email address" }).max(255);

interface NewsletterSubscriptionProps {
  source?: string;
  variant?: 'default' | 'compact' | 'hero';
}

const NewsletterSubscription = ({ source = 'website', variant = 'default' }: NewsletterSubscriptionProps) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = emailSchema.safeParse(email);
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({ 
          email: validation.data,
          source 
        });

      if (error) {
        if (error.code === '23505') {
          toast.info("You're already subscribed to our newsletter!");
          setIsSubscribed(true);
        } else {
          throw error;
        }
      } else {
        setIsSubscribed(true);
        toast.success("Welcome! You'll receive our exclusive market insights.");
        setEmail('');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className={`flex items-center justify-center gap-3 p-6 rounded-lg bg-primary/10 border border-primary/20 ${variant === 'compact' ? 'p-4' : ''}`}>
        <CheckCircle className="h-6 w-6 text-primary" />
        <span className="text-foreground font-medium">You're subscribed to exclusive insights!</span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 bg-background border-border"
          required
        />
        <Button type="submit" disabled={isLoading} size="sm">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </form>
    );
  }

  if (variant === 'hero') {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border border-primary/20 p-8 md:p-12">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="h-6 w-6 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Exclusive Access</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-display text-foreground mb-3">
            Private Market Intelligence
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md">
            Join discerning investors receiving exclusive off-market opportunities and luxury real estate insights across Monaco, Dubai, Italy & Spain.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md">
            <Input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 h-12 bg-background/80 backdrop-blur border-border text-base"
              required
            />
            <Button type="submit" disabled={isLoading} size="lg" className="h-12 px-8">
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Get Access'
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-4">
            Join 2,500+ high-net-worth individuals. Unsubscribe anytime.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6 md:p-8">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="h-5 w-5 text-primary" />
        <span className="text-sm font-medium text-primary uppercase tracking-wider">Newsletter</span>
      </div>
      <h3 className="text-xl font-display text-foreground mb-2">
        Stay Ahead of the Market
      </h3>
      <p className="text-muted-foreground text-sm mb-5">
        Receive curated insights on luxury property trends, investment opportunities, and exclusive listings.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 bg-background border-border"
          required
        />
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </form>
      <p className="text-xs text-muted-foreground mt-3">
        No spam. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default NewsletterSubscription;
